/**
 * Utility functions for determining user role.
 *
 * VRS supports three primary user types:
 * - Client: Deaf/hard-of-hearing user who requests interpretation services
 * - Interpreter: Sign language interpreter who provides interpretation
 * - Captioner: Human live captioner who can publish in-room captions
 *
 * Note: Clients can use VRS meetings normally (up to 8 participants) without
 * requesting an interpreter. The interpreter can be requested at any time.
 */
import { isVriSessionStarter } from '../../interpreter-queue/vriSessionHangup';
import { clearPersistentItems, getPersistentItem, getPersistentJson, setPersistentItem } from '../../vrs-auth/storage';
import { isUnifiedRoomModel } from '../whitelabel/functions';
/**
 * Determines if the current user is an interpreter based on URL, session, or other indicators.
 *
 * @returns {boolean} True if the user is an interpreter, false otherwise.
 */
export function isInterpreter() {
    const userRole = getPersistentItem('vrs_user_role');
    if (userRole === 'interpreter') {
        return true;
    }
    // Check URL path for interpreter-specific pages
    if (typeof window !== 'undefined' && window.location) {
        const path = window.location.pathname || '';
        if (path.includes('interpreter-dashboard') ||
            path.includes('interpreter-login') ||
            path.includes('/interpreter/')) {
            return true;
        }
        // Check URL parameters for role indication
        const urlParams = new URLSearchParams(window.location.search);
        const role = urlParams.get('role');
        if (role === 'interpreter') {
            return true;
        }
    }
    return false;
}
/**
 * Whether this interpreter's current call was taken by a colleague through a
 * transfer (interpreter-queue/middleware.ts marks it on call_transfer_accepted).
 * They stay in the room until the server releases them; leaving meanwhile
 * must not end the call for the Deaf client and the colleague.
 *
 * @returns {boolean} True once the call has been handed over.
 */
export function hasHandedOverCall() {
    return isInterpreter() && getPersistentJson('vrs_active_call')?.handedOver === true;
}
/**
 * Whether a room is a deaf-to-deaf platform call the server minted: `p2p-`
 * (a direct call, also after it grows into a group) or `group-`.
 *
 * @param {string} [roomName] - The conference room.
 * @returns {boolean}
 */
export function isPlatformCallRoom(roomName) {
    return typeof roomName === 'string' && /^(p2p|group)-/i.test(roomName);
}
/**
 * Whether this room is a group call: a `group-` room, or a `p2p-` room whose
 * stored call the server said is a group (a direct call becomes one when a
 * third person is invited). In a group call a hang-up only leaves (Robert,
 * 2026-10-05); only the host's End call for everyone ends it for all.
 *
 * @param {string} [roomName] - The conference room.
 * @returns {boolean}
 */
export function isGroupPlatformCall(roomName) {
    if (!isPlatformCallRoom(roomName)) {
        return false;
    }
    const room = String(roomName).toLowerCase();
    if (room.startsWith('group-')) {
        return true;
    }
    const call = getPersistentJson('vrs_active_call');
    return call?.groupCall === true && typeof call.roomName === 'string' && call.roomName.toLowerCase() === room;
}
/**
 * Whether the signed-in client started the VRI session in this room
 * (interpreter-queue/vriSessionHangup.ts): only their hang-up ends it for
 * everyone (Robert, 2026-10-09).
 *
 * @param {string} [roomName] - The conference room.
 * @param {string} [roomTokenRole] - The room token's role (features/base/jwt).
 * @returns {boolean}
 */
export function isVriSessionStarterIn(roomName, roomTokenRole) {
    const user = getPersistentJson('vrs_user_info');
    return isVriSessionStarter(getPersistentJson('vrs_active_call'), user?.id, getPersistentItem('vrs_user_role'), roomName ?? undefined, roomTokenRole);
}
/**
 * Determines if the current user is a captioner.
 *
 * @returns {boolean} True if the user is a captioner, false otherwise.
 */
export function isCaptioner() {
    const userRole = getPersistentItem('vrs_user_role');
    if (userRole === 'captioner') {
        return true;
    }
    if (typeof window !== 'undefined' && window.location) {
        const path = window.location.pathname || '';
        if (path.includes('captioner-profile') || path.includes('/captioner/')) {
            return true;
        }
        const urlParams = new URLSearchParams(window.location.search);
        const role = urlParams.get('role');
        if (role === 'captioner') {
            return true;
        }
    }
    return false;
}
/**
 * Determines if the current user is a client.
 *
 * @returns {boolean} True if the user is a client, false otherwise.
 */
export function isClient() {
    const userRole = getPersistentItem('vrs_user_role');
    if (userRole === 'client') {
        return true;
    }
    if (userRole === 'interpreter') {
        return false;
    }
    if (userRole === 'captioner') {
        return false;
    }
    const isClientAuth = getPersistentItem('vrs_client_auth') === 'true';
    if (isClientAuth) {
        return true;
    }
    // Check URL path for client-specific pages
    if (typeof window !== 'undefined' && window.location) {
        const path = window.location.pathname || '';
        if (path.includes('client-login') || path.includes('/client/')) {
            return true;
        }
        // Check URL parameters for role indication
        const urlParams = new URLSearchParams(window.location.search);
        const role = urlParams.get('role');
        if (role === 'client') {
            return true;
        }
        if (role === 'captioner') {
            return false;
        }
    }
    // Default to client if no specific interpreter indicators are found
    return !isInterpreter() && !isCaptioner();
}
/**
 * Gets the user role as a string.
 *
 * @returns {'interpreter' | 'client' | 'captioner'} The user's role.
 */
export function getUserRole() {
    if (isInterpreter()) {
        return 'interpreter';
    }
    if (isCaptioner()) {
        return 'captioner';
    }
    return 'client';
}
/**
 * Sets the user role in session storage.
 * This should be called when the user selects their role on the welcome page.
 *
 * @param {'interpreter' | 'client' | 'captioner'} role - The user's role.
 */
export function setUserRole(role) {
    setPersistentItem('vrs_user_role', role);
    if (role === 'client') {
        setPersistentItem('vrs_client_auth', 'true');
    }
    clearPersistentItems(['vrs_interpreter_auth']);
    if (role !== 'client') {
        clearPersistentItems(['vrs_client_auth']);
    }
}
/**
 * Clears the user role from session storage.
 */
export function clearUserRole() {
    clearPersistentItems(['vrs_user_role', 'vrs_interpreter_auth', 'vrs_client_auth']);
}
/**
 * Whether diagnostic call-screen overlays (media health, pane role captions,
 * Live badges) should render. Off by default; enabled per account from the
 * admin dashboard (debugOverlays on the profile), or ad hoc with ?vrsDebug=1
 * when the signed-in account is an admin or superadmin. The URL switch used to
 * work for anyone, putting diagnostic chrome in front of clients.
 *
 * This is a presentation gate on the stored profile, not an access control:
 * the overlays only describe this browser's own media state.
 *
 * @returns {boolean} True if debug overlays should be shown.
 */
export function areDebugOverlaysEnabled() {
    let userInfo = null;
    try {
        userInfo = JSON.parse(getPersistentItem('vrs_user_info') || '{}');
    }
    catch (e) {
        return false;
    }
    if (userInfo?.debugOverlays === true) {
        return true;
    }
    if (userInfo?.role !== 'admin' && userInfo?.role !== 'superadmin') {
        return false;
    }
    if (typeof window === 'undefined' || !window.location) {
        return false;
    }
    const vrsDebug = new URLSearchParams(window.location.search).get('vrsDebug');
    return vrsDebug === '1' || vrsDebug === 'true';
}
/**
 * Whether the microphone control should be blocked for the current call on
 * the VRS mobile app. On a VRS call with an interpreter the mic sends no
 * useful audio (voice reaches the hearing caller via the phone leg or the
 * VCO bridge), so an unmuted-looking mic is misleading. VRI calls are never
 * blocked: room audio is part of that service.
 *
 * P2P and group calls are never blocked either. Robert, 2026-10-12: "the
 * microphone (mute/unmute) in P2P calls shouldn't be grayed out. It should be
 * muted by default, but we can toggle it." The room starts muted
 * (base/media/platformCallStartMuted.ts); the person turns it on and off.
 * Before, the button stayed gray unless Settings → "Microphone in direct
 * calls" was on (off by default).
 *
 * @returns {boolean} True if the mic control should be disabled.
 */
export function isMicrophoneBlockedForCall() {
    let activeCall = {};
    try {
        activeCall = JSON.parse(getPersistentItem('vrs_active_call') || '{}');
    }
    catch (e) {
        activeCall = {};
    }
    if (activeCall?.callType === 'vri' || isUnifiedRoomModel()) {
        return false;
    }
    if (activeCall?.callType === 'vrs' || (!isInterpreter() && activeCall?.interpreterId)) {
        return true;
    }
    // Never infer phone-leg routing for an interpreter from a room name or
    // role alone. Untyped unified-room calls need live room audio.
    if (isInterpreter()) {
        return false;
    }
    return false;
}
